import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { api, type Incarico } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { Attesa, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'
import { Etichetta, Riquadro, Tabella } from '../componenti/base.tsx'
import { ProssimeMediazioni } from './mediazioni/LeMieMediazioni.tsx'
import { OggiInSede } from './agenda/OggiInSede.tsx'
import { BannerAppTelefono } from '../componenti/BannerAppTelefono.tsx'
import {
  IconaCalendario, IconaCampana, IconaDocumento, IconaEnti, IconaInterventi, IconaPersone, IconaProgetti,
  IconaRegistro,
} from '../componenti/Icone.tsx'

type Decisioni = { in_attesa: number; attesa_massima_giorni: number | null; puo_approvare: boolean }

/** GET /cruscotto/promemoria (460): ogni voce c'e' solo con il permesso dell'elenco a cui porta. */
type Promemoria = {
  schede?: { da_completare: number; da_verificare: number; vede_tutta_cidis: boolean }
  firme_terzi?: { aperte: number; scadute: number }
  servizi_da_verificare?: number
  interventi_bozza?: number
}
const PERMESSI_PROMEMORIA = ['persona.cerca', 'consenso.leggi', 'servizio_erogato.leggi', 'intervento.leggi']

type Ricontatto = {
  id: string; persona_id: string; previsto_il: string; motivo: string | null
  /** Ora facoltativa (465). */
  ora?: string | null
  nome: string; cognome: string; numero_scheda: number
  /** `count(*) over ()` della rotta di elenco: ricontatti nel periodo, prima della finestra. */
  totale?: string | number
}

/**
 * Ricontatti mostrati sul cruscotto.
 *
 * Il cruscotto e' un riepilogo, non un elenco da scorrere: si mostrano i primi e, quando ce
 * ne sono altri, si rimanda alla pagina dei ricontatti invece di impaginare qui. Prima si
 * leggeva la finestra predefinita della rotta senza dirlo, quindi dal cruscotto non si capiva
 * che oltre i primi ce ne fossero.
 */
const RICONTATTI_IN_VISTA = 8
type Notifica = { id: string; titolo: string; corpo: string | null; creata_il: string; priorita: string }

/** Dove vale un incarico, a parole: «tutta CIDIS», «Sede di Perugia», «progetto FAMI…». */
function doveVale(ambito: Incarico['ambito']): string {
  if (ambito.tipo === 'organizzazione') return 'tutta CIDIS'
  const prefisso: Record<string, string> = { area: 'area ', progetto: 'progetto ', ente: 'ente ', tipologia: '' }
  return ambito.nome ? `${prefisso[ambito.tipo] ?? ''}${ambito.nome}` : `${ambito.tipo} non più presente`
}

/** Prima pagina: le scadenze dei prossimi giorni e i collegamenti alle sezioni consentite. */
export function Cruscotto() {
  const { io, puo } = useSessione()
  const [ricontatti, setRicontatti] = useState<Ricontatto[] | null>(null)
  const [notifiche, setNotifiche] = useState<Notifica[]>([])
  const [decisioni, setDecisioni] = useState<Decisioni | null>(null)
  const [promemoria, setPromemoria] = useState<Promemoria | null>(null)

  useEffect(() => {
    if (puo('follow_up.gestisci')) {
      const entro = new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10)
      api.get<Ricontatto[]>(`/follow-up?entro=${entro}&limite=${RICONTATTI_IN_VISTA}`)
        .then(setRicontatti).catch(() => setRicontatti([]))
    } else setRicontatti([])
    api.get<{ notifiche: Notifica[] }>('/notifiche?solo_da_leggere=true&limite=5')
      .then((r) => setNotifiche(r.notifiche)).catch(() => {})
    if (puo('decisione.leggi')) {
      api.get<Decisioni>('/decisioni/conteggio').then(setDecisioni).catch(() => {})
    }
    if (PERMESSI_PROMEMORIA.some((p) => puo(p))) {
      api.get<Promemoria>('/cruscotto/promemoria').then(setPromemoria).catch(() => {})
    }
  }, [puo])

  const totaleRicontatti = Number(ricontatti?.[0]?.totale ?? ricontatti?.length ?? 0)
  const altriRicontatti = totaleRicontatti - (ricontatti?.length ?? 0)

  return (
    <>
      <Intestazione titolo="Cruscotto"
                    sottotitolo={new Date().toLocaleDateString('it-IT', {
                      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} />

      {/* L'app per il telefono (1.30.0): il codice QR dal computer, il pulsante dal telefono. */}
      <BannerAppTelefono />

      {/* Il mediatore vede per prime le sue mediazioni, con giorno e ora (indicazioni del 17/10/2026). */}
      {puo('mediazione.propria') && <ProssimeMediazioni />}

      {/* «Oggi in sede» (559): se la sede e' aperta, chi e' in turno, quanti impegni. */}
      {puo('agenda.leggi') && <OggiInSede />}

      {notifiche.length > 0 && (
        <Riquadro titolo={<span className="titolo-con-icona"><IconaCampana /> Da leggere</span>}
                  azioni={<Link to="/notifiche">Tutte le notifiche</Link>}>
          <dl className="elenco-dati">
            {notifiche.map((n) => (
              <div key={n.id} className="dato">
                <dt className="dato-con-etichetta">
                  {n.priorita === 'alta' && <Etichetta tono="attenzione">Importante</Etichetta>}
                  <span className="dato-testo">
                    <span className="dato-titolo">{n.titolo}</span>
                    {n.corpo && <span className="piccolo tenue">{n.corpo}</span>}
                  </span>
                </dt>
                <dd className="piccolo tenue">{new Date(n.creata_il).toLocaleDateString('it-IT')}</dd>
              </div>
            ))}
          </dl>
        </Riquadro>
      )}

      {promemoria && <DaSistemare promemoria={promemoria} />}

      {decisioni && decisioni.in_attesa > 0 && (
        <Riquadro denso titolo={<span className="titolo-con-icona"><IconaDocumento /> Decisioni in attesa</span>}
                  azioni={<Link to="/decisioni">{decisioni.puo_approvare ? 'Decidi' : 'Vedi il registro'}</Link>}>
          {/* puo_approvare distingue chi decide da chi legge soltanto: il promemoria di decidere
              va a chi puo' farlo, agli altri resta la sola lettura. */}
          <p className="piccolo tenue">
            {decisioni.in_attesa === 1 ? 'Una decisione attende' : decisioni.in_attesa + ' decisioni attendono'} una risposta
            {decisioni.attesa_massima_giorni != null && `; la più vecchia da ${decisioni.attesa_massima_giorni} giorni`}.
          </p>
        </Riquadro>
      )}

      {puo('follow_up.gestisci') && (
        <Riquadro denso titolo={
          <span className="titolo-con-icona">
            <IconaCalendario /> Ricontatti dei prossimi sette giorni
          </span>
        } azioni={altriRicontatti > 0
          ? <Link to="/follow-up">Tutti i ricontatti ({totaleRicontatti})</Link>
          : undefined}>
          {!ricontatti
            ? <Attesa testo="Caricamento…" />
            : (
              <Tabella intestazioni={['Quando', 'Beneficiario', 'Motivo']}
                       vuoto={<Vuoto testo="Nessun ricontatto in programma." />}>
                {ricontatti.map((r) => (
                  <tr key={r.id}>
                    <td data-etichetta="Quando">
                      {new Date(r.previsto_il).toLocaleDateString('it-IT')}{r.ora ? ` alle ${r.ora}` : ''}
                    </td>
                    <td data-etichetta="Beneficiario">
                      <Link to={`/persone/${r.persona_id}`}>{r.cognome} {r.nome}</Link>
                    </td>
                    <td data-etichetta="Motivo">{r.motivo}</td>
                  </tr>
                ))}
              </Tabella>
            )}
          {altriRicontatti > 0 && (
            <p className="nota-elenco">
              Ne sono mostrati {ricontatti!.length} su {totaleRicontatti}.{' '}
              <Link to="/follow-up">Vedi tutti i ricontatti</Link>.
            </p>
          )}
        </Riquadro>
      )}

      <div className="griglia griglia-riquadri griglia-scorciatoie">
        {puo('persona.cerca') && (
          <Scorciatoia a="/persone" icona={<IconaPersone />} titolo="Beneficiari"
                       testo="Ricerca delle schede utente e nuovi inserimenti." />
        )}
        {puo('intervento.leggi') && (
          <Scorciatoia a="/interventi" icona={<IconaInterventi />} titolo="Interventi"
                       testo="Registrazione degli interventi svolti." />
        )}
        {puo('ente.leggi') && (
          <Scorciatoia a="/enti" icona={<IconaEnti />} titolo="Enti"
                       testo="Anagrafica degli enti, con referenti e sedi CIDIS collegate." />
        )}
        {puo('progetto.leggi') && (
          <Scorciatoia a="/progetti" icona={<IconaProgetti />} titolo="Progetti"
                       testo="Anagrafica dei progetti, work package e scadenze di rendicontazione." />
        )}
        {puo('audit.leggi') && (
          <Scorciatoia a="/audit" icona={<IconaRegistro />} titolo="Registro eventi"
                       testo="Operazioni registrate, con autore e data." />
        )}
      </div>

      <Riquadro titolo="Incarichi">
        {io?.incarichi.length
          ? (
            <dl className="elenco-dati">
              {io.incarichi.map((i, n) => (
                <div key={n} className="dato">
                  <dt className="dato-con-etichetta">
                    <Etichetta tono="accento">{i.ruolo}</Etichetta>
                    <span>{i.ruoloNome ?? i.ruolo} · {doveVale(i.ambito)}</span>
                  </dt>
                  <dd className="piccolo tenue">
                    {i.soloPropri && 'solo i propri dati'}
                    {i.scadenza && ` · scade il ${new Date(i.scadenza).toLocaleDateString('it-IT')}`}
                    {!i.soloPropri && !i.scadenza && 'senza scadenza'}
                  </dd>
                </div>
              ))}
            </dl>
          )
          : <p className="tenue">Nessun incarico attivo.</p>}
        <p className="nota-modulo">
          Quello che vedi nel gestionale dipende da questi incarichi.
        </p>
      </Riquadro>
    </>
  )
}

/**
 * Contatori delle cose da sistemare (460): schede da completare e da verificare, firme di terzi,
 * servizi del registro da verificare, interventi propri in bozza. Solo i numeri, ciascuno con il
 * collegamento all'elenco; le voci a zero non si mostrano.
 */
function DaSistemare({ promemoria: p }: { promemoria: Promemoria }) {
  const voci: { a: string; nome: string; numero: number; nota?: string }[] = []
  if (p.schede?.da_completare) voci.push({ a: '/da-completare', nome: 'Schede da completare', numero: p.schede.da_completare })
  if (p.schede?.da_verificare) voci.push({ a: '/persone?da_verificare=true', nome: 'Schede da verificare', numero: p.schede.da_verificare })
  if (p.firme_terzi?.aperte) {
    voci.push({
      a: '/da-completare?vista=firme', nome: 'Firme di terzi da completare', numero: p.firme_terzi.aperte,
      nota: p.firme_terzi.scadute ? `${p.firme_terzi.scadute} con il termine scaduto` : undefined,
    })
  }
  if (p.servizi_da_verificare) {
    voci.push({ a: '/registro?da_verificare=true', nome: 'Servizi del registro da verificare', numero: p.servizi_da_verificare })
  }
  // L'elenco degli interventi filtra per stato e per «miei» (parametri stato=bozza&miei=true).
  if (p.interventi_bozza) {
    voci.push({ a: '/interventi?stato=bozza&miei=true', nome: 'I tuoi interventi in bozza', numero: p.interventi_bozza })
  }
  if (!voci.length) return null
  return (
    <Riquadro titolo={<span className="titolo-con-icona"><IconaDocumento /> Da sistemare</span>}>
      <dl className="elenco-dati">
        {voci.map((v) => (
          <div key={v.a} className="dato">
            <dt className="dato-con-etichetta">
              <Link to={v.a}>{v.nome}</Link>
              {v.nota && <Etichetta tono="attenzione">{v.nota}</Etichetta>}
            </dt>
            <dd className="mono">{v.numero}</dd>
          </div>
        ))}
      </dl>
      <p className="nota-modulo">Nel tuo ambito: quello che rientra nei tuoi incarichi.</p>
    </Riquadro>
  )
}

function Scorciatoia({ a, titolo, testo, icona }: {
  a: string; titolo: string; testo: string; icona: ReactNode
}) {
  return (
    <Link to={a} className="riquadro scorciatoia">
      <span className="scorciatoia-icona">{icona}</span>
      <h3>{titolo}</h3>
      <span className="piccolo tenue">{testo}</span>
    </Link>
  )
}
